// Path construction. Same testing rule as `scale.ts`: these are exported and
// asserted directly, never through a rendered path string.

/** Contiguous runs of measured points. A `null` closes a run and starts a new
 * one; an all-null series returns `[]` and NOTHING is drawn.
 *
 * This is `Sparkline.segments` generalised to `[x, y]` pairs, and it is the
 * single most load-bearing function in this directory. Its reasoning, verbatim
 * from that file: a line drawn flat along the axis across unmeasured days is
 * "the single most convincing way to claim 'nobody opened this' about a period
 * in which nothing was measured."
 *
 * `Sparkline` keeps its own copy on purpose. Its header says why a rule about
 * one screen's data should not be centralised where a second caller can pass a
 * pre-zeroed series and lose it; that argument still holds, so this is a
 * parallel implementation rather than a replacement.
 */
export function segments(
  points: Array<[number, number] | null>,
): Array<Array<[number, number]>> {
  const runs: Array<Array<[number, number]>> = [];
  let run: Array<[number, number]> = [];
  for (const point of points) {
    if (point === null) {
      if (run.length) runs.push(run);
      run = [];
      continue;
    }
    run.push(point);
  }
  if (run.length) runs.push(run);
  return runs;
}

/** `M x,y L x,y ...` through one run. A single point yields an empty string —
 *  draw a dot for it instead, the way `Sparkline` does. */
export function linePath(run: Array<[number, number]>): string {
  if (run.length < 2) return "";
  return run.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join("");
}

/** A filled area between one run and `baseline`. Empty for a single point. */
export function areaPath(run: Array<[number, number]>, baseline: number): string {
  if (run.length < 2) return "";
  const forward = run.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x},${y}`).join("");
  return `${forward}L${run[run.length - 1][0]},${baseline}L${run[0][0]},${baseline}Z`;
}

/** Cartesian coordinates of a point on a dial.
 *
 * THE CONVENTION IS CLOCKWISE FROM TWELVE O'CLOCK, matching how anyone reads a
 * clock face — slot 0 at the top, increasing to the right. Note this is NOT
 * the trigonometric convention (0 at three o'clock, counter-clockwise), and
 * mixing the two puts labels ninety degrees away from the wedges they name.
 * Every position on every radial chart here goes through this one function so
 * that cannot happen.
 */
export function polar(turn: number, radius: number): [number, number] {
  const angle = turn * 2 * Math.PI;
  return [Math.sin(angle) * radius, -Math.cos(angle) * radius];
}

/** An annular wedge from `from` to `to`, both as fractions of a full turn. */
export function arcPath(
  from: number,
  to: number,
  inner: number,
  outer: number,
): string {
  const [x0o, y0o] = polar(from, outer);
  const [x1o, y1o] = polar(to, outer);
  const [x1i, y1i] = polar(to, inner);
  const [x0i, y0i] = polar(from, inner);
  const large = to - from > 0.5 ? 1 : 0;
  return [
    `M${x0o},${y0o}`,
    `A${outer},${outer} 0 ${large} 1 ${x1o},${y1o}`,
    `L${x1i},${y1i}`,
    `A${inner},${inner} 0 ${large} 0 ${x0i},${y0i}`,
    "Z",
  ].join("");
}

/** A stepped closed outline through per-slot radii — the second series on a
 *  dial, drawn as a boundary so it cannot be hidden underneath the first. */
export function ringPath(radii: Array<number | null>, slots: number): string {
  const parts: string[] = [];
  let open = false;
  for (let slot = 0; slot < slots; slot += 1) {
    const radius = radii[slot];
    if (radius === null || radius === undefined) {
      open = false;
      continue;
    }
    const [xa, ya] = polar(slot / slots, radius);
    const [xb, yb] = polar((slot + 1) / slots, radius);
    parts.push(`${open ? "L" : "M"}${xa},${ya}`, `L${xb},${yb}`);
    open = true;
  }
  return parts.join("");
}
